<template lang="pug">
  .gmeet-gmail-modal-overlay(v-if="visible", @click.self="onCancel")
    .gmeet-gmail-modal
      h3.gmeet-gmail-modal__title 設定 Google Meet 信箱
      p.gmeet-gmail-modal__msg {{ message }}

      .gmeet-form-group
        label.gmeet-form-label(for="gmeet-email-input") Google Meet 用 Email
        input.gmeet-form-input(
          id="gmeet-email-input",
          type="email",
          v-model="email",
          placeholder="example@gmail.com",
          autocomplete="email"
        )
        small.gmeet-form-hint 必須為已登入 Google 帳號的 Email（建議使用 Gmail）

      .gmeet-form-group
        label.gmeet-consent-label(:class="{ 'is-checked': agreed }")
          input.gmeet-consent-checkbox(type="checkbox", v-model="agreed")
          span.gmeet-consent-text 我同意提供 Email 用於 Google Meet 視訊會議加入認證
        .gmeet-consent-note
          | 詳細說明請參考
          |
          a.gmeet-consent-link(href="https://www.edu1n1.com/web/privacy", target="_blank", rel="noopener noreferrer") 隱私權政策

      p.gmeet-form-error(v-if="errMsg") {{ errMsg }}

      .gmeet-gmail-modal__actions
        button.gmeet-btn.gmeet-btn--ghost(type="button", @click="onCancel", :disabled="submitting") 取消
        button.gmeet-btn.gmeet-btn--primary(
          type="button",
          @click="onSubmit",
          :disabled="submitting || !canSubmit"
        )
          | {{ submitting ? '送出中...' : '儲存並繼續' }}
</template>

<script>
import { MeetingEventBus } from './mixins/meeting'

/**
 * Gmail 彈窗元件 — 訂閱 EventBus 'show-gmail-modal' 事件自動顯示
 *
 * 對應計畫 §4.4 / §5.5（仿 course-apis/web-pack 同名元件移植）
 *
 * 使用方式：在 App.vue 全域註冊一次：
 *   <student-gmail-modal />
 * 之後任何頁面只要透過 meeting mixin 觸發即會顯示
 */
export default {
  name: 'StudentGmailModal',
  data () {
    return {
      visible: false,
      message: '',
      onSuccessCallback: null,
      email: '',
      agreed: false,
      submitting: false,
      errMsg: ''
    }
  },
  computed: {
    canSubmit () {
      return this.email && this.agreed && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email)
    }
  },
  mounted () {
    MeetingEventBus.$on('show-gmail-modal', this.show)
  },
  beforeDestroy () {
    MeetingEventBus.$off('show-gmail-modal', this.show)
  },
  methods: {
    show (payload) {
      this.message = (payload && payload.message) || '請先設定 Gmail'
      this.onSuccessCallback = (payload && payload.onSuccess) || null
      this.email = ''
      this.agreed = false
      this.errMsg = ''
      this.submitting = false
      this.visible = true
    },
    onCancel () {
      if (this.submitting) return
      this.visible = false
      // 學生取消 → 通報後端記錄（觀測 UX 流暢度）。失敗不影響 UX。
      this.$http.post('log/gmeetAbandon', {}).then(function () {}, function () {})
    },
    onSubmit () {
      if (!this.canSubmit || this.submitting) return
      this.submitting = true
      this.errMsg = ''

      this.$http.post('student/center/updateGmail', {
        email: this.email,
        consent_agreed: 1,
        _intent: 'gmeet_required'
      }).then((res) => {
        if (res.body && res.body.error_code === 0) {
          this.visible = false
          this.submitting = false
          if (typeof this.onSuccessCallback === 'function') {
            this.onSuccessCallback(res.body.data)
          }
        } else {
          this.errMsg = (res.body && res.body.message) || '儲存失敗，請重試'
          this.submitting = false
        }
      }, (err) => {
        this.errMsg = (err && err.message) || '網路錯誤，請重試'
        this.submitting = false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.gmeet-gmail-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(20, 30, 60, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
}

.gmeet-gmail-modal {
  background: #fff;
  border-radius: 8px;
  padding: 1.75rem 1.75rem 1.5rem;
  width: 100%;
  max-width: 460px;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.22);
  color: #2f384f;
  font-size: 0.9rem;
  line-height: 1.55;

  &__title {
    margin: 0 0 0.6rem;
    font-size: 1.15rem;
    font-weight: 600;
    color: #1f2a44;
  }

  &__msg {
    margin: 0 0 1.25rem;
    color: #545d75;
    font-size: 0.85rem;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 0.75rem;
    margin-top: 1.5rem;
  }
}

.gmeet-form-group {
  margin-bottom: 1rem;

  & + & {
    margin-top: 0;
  }
}

.gmeet-form-label {
  display: block;
  margin-bottom: 0.4rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #2f384f;
  letter-spacing: 0.3px;
}

.gmeet-form-input {
  display: block;
  width: 100%;
  box-sizing: border-box;
  padding: 0.6rem 0.75rem;
  font-size: 0.9rem;
  color: #2f384f;
  background: #fff;
  border: 1px solid #d0d7e2;
  border-radius: 4px;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: #b8c0cf;
  }
  &:focus {
    border-color: #2e50ff;
    box-shadow: 0 0 0 3px rgba(46, 80, 255, 0.12);
  }
}

.gmeet-form-hint {
  display: block;
  margin-top: 0.35rem;
  font-size: 0.72rem;
  color: #8a94a6;
  line-height: 1.45;
}

.gmeet-consent-label {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.6rem 0.7rem;
  cursor: pointer;
  user-select: none;
  background: #fafbfd;
  border: 1px solid #e2e6ee;
  border-radius: 4px;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  &:hover {
    border-color: #2e50ff;
    background: #f3f6ff;
  }
  &.is-checked {
    border-color: #2e50ff;
    background: #f3f6ff;
  }
}

// 反 reset:全域 ui.sass 對 input 強制 appearance:none + 透明 border,
// 會讓 checkbox 變成完全看不見的透明方塊。這裡在 scoped 範圍把原生外觀補回來。
.gmeet-consent-checkbox {
  flex-shrink: 0;
  width: 1rem;
  height: 1rem;
  margin: 0.18rem 0 0;
  cursor: pointer;
  appearance: auto;
  -webkit-appearance: auto;
  -moz-appearance: auto;
  border: 1px solid #b8c0cf;
  background: #fff;
  accent-color: #2e50ff;
}

.gmeet-consent-text {
  font-size: 0.82rem;
  color: #2f384f;
  line-height: 1.5;
}

.gmeet-consent-note {
  margin-top: 0.5rem;
  padding-left: 0.2rem;
  font-size: 0.72rem;
  color: #8a94a6;
}

.gmeet-consent-link {
  color: #2e50ff;
  font-weight: 600;
  text-decoration: underline;
  &:hover {
    color: #1f3ee0;
  }
}

.gmeet-form-error {
  margin: 0.75rem 0 0;
  padding: 0.5rem 0.7rem;
  font-size: 0.78rem;
  color: #c0392b;
  background: rgba(192, 57, 43, 0.08);
  border-radius: 4px;
}

.gmeet-btn {
  appearance: none;
  border: none;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 500;
  letter-spacing: 0.5px;
  padding: 0.55rem 1.25rem;
  border-radius: 4px;
  transition: background-color 0.15s ease, color 0.15s ease, opacity 0.15s ease;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  &--primary {
    background: #2e50ff;
    color: #fff;
    &:hover:not(:disabled) { background: #1f3ee0; }
  }

  &--ghost {
    background: transparent;
    color: #545d75;
    &:hover:not(:disabled) {
      background: #f1f4fa;
      color: #2f384f;
    }
  }
}
</style>
